import { Box, CircularProgress, Typography } from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ColorfulRole from "../../../components/UIToolkit/ColorfulRole/ColorfulRole";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { CostDeepDiveUnit, type OtMode, type StaffMemberCostBreakdown } from "../../../utils/models";
import { formatCurrency } from "../../../utils/utilFunctions";
import { MC_EXPANDED_ROW_BG, MC_GRID_SX } from "../utils";
import { useGetDailyCostBreakdown } from "./hooks";
import { formatHours } from "./utils";

type Props = {
	date: string;
	endDate?: string;
	isActual: boolean;
	roles?: string[];
	jobs?: string[];
	shiftHours?: string[];
	laborCost?: boolean;
	hasBonuses?: boolean;
	hasDailyOt?: boolean;
	/** DAT-289: the department configures a consecutive-day rule. */
	hasConsecutiveOt?: boolean;
	includeLateScheduled?: boolean;
	unit?: CostDeepDiveUnit;
	otMode?: OtMode;
	estimateMissingWage: boolean;
};

export const CostDeepDiveExpandedRow: FC<Props> = ({
	date,
	endDate,
	isActual,
	roles,
	jobs,
	shiftHours,
	laborCost,
	hasBonuses,
	hasDailyOt,
	hasConsecutiveOt,
	includeLateScheduled,
	unit = CostDeepDiveUnit.Cost,
	otMode,
	estimateMissingWage,
}) => {
	const { t } = useTranslation();
	const formatPersonName = useFormatPersonName();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const { data, isLoading } = useGetDailyCostBreakdown({
		date,
		endDate,
		enabled: true,
		isActual,
		roles,
		jobs,
		shiftHours,
		laborCost,
		includeLateScheduled,
		otMode,
		estimateMissingWage,
	});

	const columns: GridColDef<StaffMemberCostBreakdown>[] = useMemo(() => {
		const isHours = unit === CostDeepDiveUnit.Hours;
		const showJobInRoleCell = jobManagementEnabled;
		const formatValue = (value: number) => (isHours ? formatHours(value) : formatCurrency(value, 1));
		// Bonus shows in any unit mode (incentive pay, in $) so hide-wage users still see it. PTO is cost-mode only.
		const includePtoCol = !isHours && !!laborCost;

		return [
			{
				field: "role",
				headerName: showJobInRoleCell ? t("job") : t("cost.deep.dive.role"),
				flex: 0.6,
				display: "flex",
				renderCell: (params: GridRenderCellParams<StaffMemberCostBreakdown>) => {
					if (showJobInRoleCell && params.row.jobDisplayName) {
						return <Typography variant="body2">{params.row.jobDisplayName}</Typography>;
					}
					return <ColorfulRole role={params.row.role} />;
				},
			},
			{
				field: "name",
				headerName: t("cost.deep.dive.name"),
				flex: 1.5,
				valueGetter: (_value: unknown, row: StaffMemberCostBreakdown) => formatPersonName(row),
				sortComparator: (v1: string, v2: string) => v1.localeCompare(v2),
			},
			{
				field: isHours ? "baseHours" : "baseCost",
				headerName: isHours ? t("cost.deep.dive.hours.base") : t("cost.deep.dive.base"),
				flex: 1,
				align: "center",
				headerAlign: "center",
				type: "number",
				valueFormatter: formatValue,
			},
			...(hasDailyOt
				? [
						{
							field: isHours ? "dailyOtHours" : "dailyOtCost",
							headerName: isHours ? t("cost.deep.dive.hours.daily.ot") : t("cost.deep.dive.daily.ot"),
							flex: 1,
							align: "center" as const,
							headerAlign: "center" as const,
							type: "number" as const,
							valueFormatter: formatValue,
						},
					]
				: []),
			{
				field: isHours ? "weeklyOtHours" : "weeklyOtCost",
				headerName: isHours ? t("cost.deep.dive.hours.weekly.ot") : t("cost.deep.dive.weekly.ot"),
				flex: 1,
				align: "center",
				headerAlign: "center",
				type: "number",
				valueFormatter: formatValue,
			},
			...(hasConsecutiveOt
				? [
						{
							field: isHours ? "consecutiveOtHours" : "consecutiveOtCost",
							headerName: isHours ? t("cost.deep.dive.hours.consecutive.ot") : t("cost.deep.dive.consecutive.ot"),
							flex: 1,
							align: "center" as const,
							headerAlign: "center" as const,
							type: "number" as const,
							valueFormatter: formatValue,
						},
					]
				: []),
			{
				field: isHours ? "agencyHours" : "agencyCost",
				headerName: isHours ? t("cost.deep.dive.hours.agency") : t("cost.deep.dive.agency"),
				flex: 1,
				align: "center",
				headerAlign: "center",
				type: "number",
				valueFormatter: formatValue,
			},
			...(hasBonuses
				? [
						{
							field: "bonusCost",
							headerName: t("cost.deep.dive.bonus"),
							flex: 1,
							align: "center" as const,
							headerAlign: "center" as const,
							type: "number" as const,
							valueFormatter: (value: number) => formatCurrency(value, 1),
						},
					]
				: []),
			...(includePtoCol
				? [
						{
							field: "ptoCost",
							headerName: t("pto"),
							flex: 1,
							align: "center" as const,
							headerAlign: "center" as const,
							type: "number" as const,
							valueFormatter: (value: number) => formatCurrency(value, 1),
						},
					]
				: []),
			{
				field: isHours ? "totalHours" : "totalCost",
				headerName: isHours ? t("cost.deep.dive.hours.total") : t("cost.deep.dive.total"),
				flex: 1,
				align: "center",
				headerAlign: "center",
				type: "number",
				display: "flex",
				renderCell: (params: GridRenderCellParams<StaffMemberCostBreakdown>) => (
					<Typography variant="body2" sx={{ fontWeight: 600, lineHeight: "inherit" }}>
						{formatValue(isHours ? params.row.totalHours : params.row.totalCost)}
					</Typography>
				),
			},
		];
	}, [t, laborCost, hasBonuses, hasDailyOt, hasConsecutiveOt, unit, jobManagementEnabled, formatPersonName]);

	if (isLoading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					py: 2,
				}}
			>
				<CircularProgress size={24} />
			</Box>
		);
	}

	if (!data?.staff || data?.staff.length === 0) {
		return (
			<Box
				sx={{
					py: 2,
					px: 4,
				}}
			>
				<Typography
					variant="body2"
					sx={{
						color: "text.secondary",
					}}
				>
					{t("cost.deep.dive.no.data")}
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ bgcolor: MC_EXPANDED_ROW_BG, py: 1, px: 4, width: "100%", boxSizing: "border-box" }}>
			<DataGrid
				rows={data.staff}
				columns={columns}
				getRowId={(row) => row.nurseId}
				initialState={{
					sorting: {
						sortModel: [{ field: unit === CostDeepDiveUnit.Hours ? "totalHours" : "totalCost", sort: "desc" }],
					},
				}}
				density="compact"
				disableColumnMenu
				disableColumnFilter
				hideFooter
				rowSelection={false}
				sx={{ ...MC_GRID_SX, bgcolor: "transparent" }}
			/>
		</Box>
	);
};
